export interface ChatMessage {
  id: string;
  text: string;
  senderId: string;
  createdAt: string;
  fileUrl?: string;
  fileName?: string;
  sender: {
    name: string;
  };
}

export class ChatService {
  private messages: ChatMessage[] = [];
  private lastMessageTime: string | null = null;
  private intervalId: any = null;
  private listeners: ((messages: ChatMessage[]) => void)[] = [];
  private isPolling = false;

  constructor(
    private apiUrl: string,
    private appointmentId: string,
    private userId: string,
    private token: string
  ) {}

  subscribe(listener: (messages: ChatMessage[]) => void) {
    this.listeners.push(listener);
    // Send current messages immediately upon subscription
    listener(this.messages);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private notify() {
    this.listeners.forEach((l) => l(this.messages));
  }

  startPolling(intervalMs = 2500) {
    if (this.isPolling) return;

    this.isPolling = true;
    this.fetchMessages();
    this.intervalId = setInterval(() => this.fetchMessages(), intervalMs);
  }

  stopPolling() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isPolling = false;
  }

  async fetchMessages() {
    try {
      const params = new URLSearchParams({
        token: this.token,
        appointmentId: this.appointmentId,
      });

      if (this.lastMessageTime) {
        params.append("since", this.lastMessageTime);
      }

      const res = await fetch(`${this.apiUrl}/chat?${params}`);
      if (!res.ok) return;

      const newMessages: ChatMessage[] = await res.json();

      if (newMessages.length > 0) {
        this.lastMessageTime = newMessages[newMessages.length - 1].createdAt;
        
        const existingIds = new Set(this.messages.map((m) => m.id));
        const filtered = newMessages.filter((m) => !existingIds.has(m.id));
        
        if (filtered.length > 0) {
          this.messages = [...this.messages, ...filtered];
          this.notify();
        }
      }
    } catch (error) {
      console.error("Error polling messages:", error);
    }
  }

  async uploadFile(file: File): Promise<string> {
    const presignRes = await fetch(`${this.apiUrl}/chat/upload/presign?token=${this.token}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        appointmentId: this.appointmentId,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
      }),
    });

    if (!presignRes.ok) throw new Error("Error obtaining upload URL");
    const { uploadUrl, fileUrl } = await presignRes.json();

    const uploadRes = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });

    if (!uploadRes.ok) throw new Error("Error uploading file to cloud");

    return fileUrl;
  }

  async sendMessage(text: string, fileUrl?: string, fileName?: string, fileType?: string) {
    const body: any = {
      appointmentId: this.appointmentId,
      text,
    };

    if (fileUrl) {
      body.fileUrl = fileUrl;
      body.fileName = fileName;
      body.fileType = fileType;
    }

    const res = await fetch(`${this.apiUrl}/chat?token=${this.token}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!res.ok) throw new Error("Error sending message");

    this.fetchMessages();
  }
  
  getUserId() {
      return this.userId;
  }
}
